import React from "react"
import { Linking, SafeAreaView, ScrollView, View } from "react-native"
import { Card } from "@/components/reusables/ui/card"
import { Button } from "@/components/reusables/ui/button"
import { Badge } from "@/components/reusables/ui/badge"
import BFText, { H4, H5, H6, Small } from "@/components/standard/BFText"
import { Horizontal, Vertical } from "@/components/standard/BFLayout"
import { Separator } from "@/components/reusables/ui/separator"
import { cn } from "@/lib/utils"
import CheckCircle from "phosphor-react-native/src/icons/CheckCircle"
import Clock from "phosphor-react-native/src/icons/Clock"
import MoneyWavy from "phosphor-react-native/src/icons/MoneyWavy"
import { BFScreenHeader } from "@/components/standard/screen/BFScreenHeader"
import ArrowSquareOut from "phosphor-react-native/src/icons/ArrowSquareOut"
import { BFLink } from "@/components/buttons/Links"
import { loanDashboardQueryOptions } from "@/queries/loan-dashboard"
import { useQuery } from "@tanstack/react-query"
import { useLoanContext } from "@/screens/loans/components/LoanContext"
import { createComponentClickedEvent, track } from "@/analytics/events/base"
import { formatCurrency } from "@/screens/draws/utils"
import { DrawsSummary } from "@/screens/draws/components/DrawsSummary"

// Get status badge component based on draw status
const getStatusBadge = (status: string, date?: string | null) => {
  switch (status) {
    case "DRAFT":
      return (
        <Badge variant="outline">
          <H6>Draft</H6>
        </Badge>
      )
    case "IN_REVIEW":
      return (
        <Badge variant="secondary">
          <Clock color="currentColor" size={16} />
          <H6>In Review</H6>
        </Badge>
      )
    case "APPROVED":
      return (
        <Badge variant="default">
          <CheckCircle color="currentColor" size={16} />
          <H6>Approved</H6>
        </Badge>
      )
    case "COMPLETED":
      return <Small className="text-muted-foreground">Funded {date}</Small>
    default:
      return null
  }
}

export default function DrawsScreen() {
  const { loanId } = useLoanContext()
  const loanDetailsQuery = useQuery({
    ...loanDashboardQueryOptions(loanId),
    select: (data) => {
      return data?.postClose?.drawsBudget
    },
  })

  if (!loanDetailsQuery?.data) {
    return null
  }

  const drawsBudget = loanDetailsQuery.data

  function EmptyState() {
    return (
      <Vertical
        className={"items-center justify-center flex-1 gap-2 min-h-full"}
      >
        <MoneyWavy size={32} />
        <H4>Ready to access your funds?</H4>
        <BFLink
          showsIcon={true}
          dark={false}
          size={"md"}
          url={drawsBudget.trustpointNewDrawUrl!}
          testId={"first-draw"}
          text="Submit your first draw request"
        />
      </Vertical>
    )
  }

  function DrawsList() {
    return (
      <>
        <Horizontal className={"justify-between items-center mb-2"}>
          <H4>Draws History</H4>
          <Button
            className=""
            variant="outline"
            onPress={() => {
              track(
                createComponentClickedEvent({
                  current_screen: "Loan Rehab Budget",
                  component_id: "new-draw",
                  component_type: "button",
                  component_function: drawsBudget.trustpointNewDrawUrl!,
                  action_taken: "link pressed",
                  component_text: "New Draw",
                })
              )

              void Linking.openURL(drawsBudget.trustpointNewDrawUrl!)
            }}
          >
            <BFText>New Draw</BFText>
            <ArrowSquareOut size={16} />
          </Button>
        </Horizontal>

        <Card className="border-border px-5">
          {drawsBudget.draws?.map((draw, index) => (
            <React.Fragment key={draw.id}>
              <View className="py-7 flex-row justify-between items-start">
                <View
                  className={cn(
                    "gap-2",
                    draw.status === "COMPLETED" ? "flex-col gap-0" : "flex-row"
                  )}
                >
                  <H5 className="text-foreground">Draw #{draw.number}</H5>
                  {getStatusBadge(draw.status!, draw.fundedDate)}
                </View>
                <H5>{formatCurrency(draw.amount)}</H5>
              </View>
              {index < (drawsBudget.draws?.length || 0) - 1 && <Separator />}
            </React.Fragment>
          ))}
        </Card>
      </>
    )
  }

  return (
    <SafeAreaView className="flex-1 bg-background font-spacegrotesk ">
      <BFScreenHeader title={"Rehab Draws"} />

      <ScrollView className="flex-1 flex-col gap-5">
        <View className={"px-5 py-10 bg-card"}>
          <DrawsSummary
            budgetedLeft={drawsBudget.budgetedLeft!}
            budgetedTotal={drawsBudget.budgetedTotal!}
            completedDrawsTotal={drawsBudget.completedDrawsTotal!}
            pendingDrawsTotal={drawsBudget.pendingDrawsTotal!}
          />
        </View>

        <Separator className={"my-5"} />

        <View className={"px-5"}>
          {(drawsBudget.draws?.length || 0) > 0 && <DrawsList />}
          {(!drawsBudget.draws || drawsBudget.draws.length === 0) && (
            <EmptyState />
          )}
        </View>
      </ScrollView>
    </SafeAreaView>
  )
}
